import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ruler, Boxes, Droplets, Workflow, Cpu, Factory, GitCompare, Gauge, Lightbulb } from "lucide-react";
import TopBar from "../components/shop/TopBar";
import Reveal from "../components/Reveal";
import { TesterGA, TesterCutaway, TesterExploded, TesterVariants } from "../components/shop/TesterDrawings";
import { TESTER_IMG, DIM, COMPONENTS, GRP_META, PROBES, STRIP_A, STRIP_B, STRIP_C, STRIP_LOGIC, STRIP_PROMPT, VARIANTS, WORKFLOW } from "../data/build";

const SLOTS = [{ k: "A", label: "6 IN 1" }, { k: "B", label: "PHOSPHATE" }, { k: "C", label: "MAGNESIUM" }];

const LINKS = [
  { to: "/shop", label: "Pool Shop" },
  { to: "/shop/build", label: "Build Spec" },
  { to: "/shop/interior", label: "Interior" },
  { to: "/shop/brief", label: "Brief / RFQ" },
  { to: "/software", label: "Software" },
  { to: "/home", label: "Home System" },
];

const VIEWS = [
  { img: TESTER_IMG.front, t: "Front ¾", d: "Screen · aqua LED · 3 labelled slots + prompt lights" },
  { img: TESTER_IMG.rear, t: "Rear", d: "Ports · vent · service cover" },
];

function Head({ icon: Icon, eyebrow, title, sub }) {
  return (
    <Reveal>
      <div className="max-w-3xl">
        <p className="eyebrow mb-3 flex items-center gap-2"><Icon className="h-3.5 w-3.5" /> {eyebrow}</p>
        <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
        {sub && <p className="mt-3 text-slate-600 leading-relaxed">{sub}</p>}
      </div>
    </Reveal>
  );
}

export default function ShopBuild() {
  return (
    <div className="min-h-screen bg-[#FAFCFD]" data-testid="shop-build-page">
      <TopBar links={LINKS} />

      {/* Hero */}
      <section className="relative pt-14 pb-12 md:pt-20 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sky-50/70 to-white" />
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-mono2 text-sky-700 mb-5">
              <Factory className="h-3.5 w-3.5" /> Manufacturer build spec · Project 1
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              The desktop tester, <span className="aqua-text">fully specified</span>
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              A probe + strip-reader hybrid — no liquid reagents, no cuvettes, no dosing pumps. Four continuous probes,
              three top-loaded strips read submerged in a self-cleaning tank, in one spill-proof benchtop unit.
              Ready to hand to a manufacturer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Exterior views */}
      <section className="pb-8">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={Boxes} eyebrow="3D · front & rear" title="Exterior views" />
          <div className="mt-8 grid sm:grid-cols-2 gap-5 max-w-4xl">
            {VIEWS.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.05}>
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden hw-card" data-testid={`view-${i}`}>
                  <div className="bg-gradient-to-b from-slate-50 to-white p-3 grid-dots">
                    <img src={v.img} alt={v.t} className="w-full h-64 object-contain" />
                  </div>
                  <div className="p-4"><p className="font-display font-bold text-slate-900">{v.t}</p><p className="text-sm text-slate-500">{v.d}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Dimensions / GA */}
      <section className="py-14 bg-white border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={Ruler} eyebrow="True size" title="Dimensioned general arrangement" sub="Proposed envelope sized to house the tank, probe block and three strip readers — to be validated in prototyping." />
          <div className="mt-6 flex flex-wrap gap-2">
            {[`${DIM.w} × ${DIM.h} × ${DIM.d} mm`, DIM.weight, DIM.screen, DIM.probes, DIM.strips, DIM.battery, DIM.panel].map((s) => (
              <span key={s} className="text-xs font-mono2 px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">{s}</span>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-4 md:p-8 shadow-[0_40px_80px_-50px_rgba(3,105,161,0.4)]"><TesterGA /></div>
          </Reveal>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-14 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={Workflow} eyebrow="Operator workflow" title="Strip in, pour, read — self-cleans between customers" sub="Strips load from the top and hang into the tank so their pads sit submerged; there are no side entries to leak." />
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {WORKFLOW.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05}>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full" data-testid={`workflow-${i}`}>
                  <span className="font-mono2 text-sm text-sky-600 font-bold">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-display font-bold text-slate-900 mt-1">{s.t}</p>
                  <p className="text-sm text-slate-500 mt-1">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sensing architecture */}
      <section className="py-14 bg-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={Cpu} eyebrow="How it measures" title="4 probes + 3 strips — with smart detection" sub="Probes are definitive for the four fast-drifting parameters; strips cover what a probe physically can't. The unit senses which of the three strips are loaded and reports accordingly." />
          <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {/* Probes */}
            <Reveal>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full" data-testid="arch-probes">
                <div className="flex items-center gap-2 mb-3"><Gauge className="h-4 w-4 text-sky-600" /><h3 className="font-display font-bold text-slate-900">Probe block — continuous</h3></div>
                <ul className="space-y-2.5">
                  {PROBES.map((p) => (
                    <li key={p.p} className="flex items-center gap-3 text-sm">
                      <span className="h-6 w-8 rounded-md flex items-center justify-center font-mono2 text-[11px] font-bold text-white shrink-0" style={{ background: p.color }}>{p.p}</span>
                      <div><span className="font-semibold text-slate-800">{p.name}</span><span className="text-slate-400 font-mono2 text-xs block">{p.method}</span></div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            {/* Strip A */}
            <Reveal delay={0.06}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full" data-testid="arch-strip-a">
                <div className="flex items-center gap-2 mb-3"><span className="h-6 w-6 rounded-md bg-slate-900 text-white flex items-center justify-center font-mono2 text-[11px] font-bold">A</span><h3 className="font-display font-bold text-slate-900">{STRIP_A.label}</h3></div>
                <ul className="space-y-2">
                  {STRIP_A.reads.map((r) => (
                    <li key={r.t} className="text-sm"><span className="font-semibold text-slate-800">{r.t}</span> <span className="text-slate-400">— {r.note}</span></li>
                  ))}
                </ul>
              </div>
            </Reveal>
            {/* Strip B */}
            <Reveal delay={0.12}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full" data-testid="arch-strip-b">
                <div className="flex items-center gap-2 mb-3"><span className="h-6 w-6 rounded-md bg-slate-900 text-white flex items-center justify-center font-mono2 text-[11px] font-bold">B</span><h3 className="font-display font-bold text-slate-900">{STRIP_B.label}</h3></div>
                <ul className="space-y-2">
                  {STRIP_B.reads.map((r) => (
                    <li key={r.t} className="text-sm"><span className="font-semibold text-slate-800">{r.t}</span> <span className="text-slate-400">— {r.note}</span></li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-slate-400">A second dedicated lane because phosphate isn't offered on universal strips.</p>
              </div>
            </Reveal>
            {/* Strip C */}
            <Reveal delay={0.18}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full" data-testid="arch-strip-c">
                <div className="flex items-center gap-2 mb-3"><span className="h-6 w-6 rounded-md bg-slate-900 text-white flex items-center justify-center font-mono2 text-[11px] font-bold">C</span><h3 className="font-display font-bold text-slate-900">{STRIP_C.label}</h3></div>
                <ul className="space-y-2">
                  {STRIP_C.reads.map((r) => (
                    <li key={r.t} className="text-sm"><span className="font-semibold text-slate-800">{r.t}</span> <span className="text-slate-400">— {r.note}</span></li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-slate-400">A third lane for mineral / magnesium pools, which the salt probe alone can't resolve.</p>
              </div>
            </Reveal>
          </div>

          {/* Smart detection table */}
          <Reveal delay={0.1}>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="strip-logic">
              <h3 className="font-display font-bold text-slate-900 mb-3">Smart strip detection</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {STRIP_LOGIC.map((s) => (
                  <div key={s.case} className="rounded-xl border border-slate-200 bg-white p-4">
                    <p className="font-mono2 text-xs text-sky-700 font-bold">{s.case}</p>
                    <p className="text-sm text-slate-600 mt-1">{s.out}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Guided strip prompt */}
      <section className="py-14 bg-white border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={Lightbulb} eyebrow="Guided prompt" title="The tester tells you which strips to insert" sub="Pull up the client and their stored pool profile lights the slots to use — a small indicator LED in front of each of the three strip slots. Standard water = 6-in-1 only; a leafy area also lights Phosphate; a mineral pool lights Magnesium. No guessing, no wasted strips." />

          {/* three slot LEDs (demo = standard test) */}
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-2xl" data-testid="prompt-slots">
            {SLOTS.map((s) => {
              const lit = s.k === "A";
              return (
                <div key={s.k} className={`rounded-2xl border p-5 text-center transition-colors ${lit ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-white"}`} data-testid={`prompt-slot-${s.k}`}>
                  <span className="mx-auto mb-3 block h-4 w-4 rounded-full" style={lit ? { background: "#22D3EE", boxShadow: "0 0 10px 2px rgba(34,211,238,0.7)" } : { background: "#CBD5E1" }} data-testid={`prompt-led-${s.k}`} />
                  <p className="font-mono2 text-[11px] tracking-widest text-slate-500">SLOT {s.k}</p>
                  <p className="font-display font-bold text-slate-900 text-sm mt-0.5">{s.label}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{lit ? "insert this strip" : "not needed"}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-slate-400 max-w-2xl">Shown: a standard test — only the 6-in-1 light is on.</p>

          {/* profile → lights table */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="prompt-logic">
            <h3 className="font-display font-bold text-slate-900 mb-4">Client profile → slot lights</h3>
            <div className="space-y-2.5">
              {STRIP_PROMPT.map((p) => (
                <div key={p.profile} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-xl border border-slate-200 bg-white p-3.5">
                  <span className="font-semibold text-slate-800 text-sm sm:w-64 shrink-0">{p.profile}</span>
                  <span className="flex gap-1.5 shrink-0">
                    {SLOTS.map((s) => {
                      const on = p.lights.includes(s.k);
                      return <span key={s.k} title={s.label} className="inline-flex items-center gap-1 text-[10px] font-mono2 px-2 py-1 rounded-full border" style={on ? { background: "#ECFEFF", borderColor: "#22D3EE", color: "#0891B2" } : { background: "#F8FAFC", borderColor: "#E2E8F0", color: "#94A3B8" }}><span className="h-2 w-2 rounded-full" style={{ background: on ? "#22D3EE" : "#CBD5E1" }} />{s.k}</span>;
                    })}
                  </span>
                  <span className="text-sm text-slate-500">{p.note}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-400">Profiles are read from the shop's client database (software layer, built after the hardware design is locked). The strip-present sensors then confirm the right strips were actually loaded.</p>
          </div>
        </div>
      </section>

      {/* Internal cutaway */}
      <section className="py-14 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={Droplets} eyebrow="Cover removed" title="Internal cutaway — strips submerged in the tank" sub="Wet measurement path isolated from dry electronics. Numbers map to the component table below." />
          <Reveal delay={0.1}><div className="mt-6 rounded-3xl border border-slate-200 bg-white p-4 md:p-8"><TesterCutaway /></div></Reveal>
        </div>
      </section>

      {/* Variants */}
      <section className="py-14 bg-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={GitCompare} eyebrow="Two build variants" title="Countertop vs portable — same core, different plumbing" sub="Identical probe + strip core. They differ only in how rinse water and waste are handled." />
          <div className="mt-6 grid lg:grid-cols-2 gap-4">
            {VARIANTS.map((v, i) => (
              <Reveal key={v.key} delay={i * 0.06}>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 h-full" data-testid={`variant-${v.key}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} />
                    <h3 className="font-display font-bold text-slate-900">{v.name}</h3>
                    <span className="text-xs font-mono2 text-slate-400">· {v.tag}</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">{v.note}</p>
                  <dl className="grid grid-cols-1 gap-2 text-sm">
                    {[["Rinse", v.rinse], ["Waste", v.waste], ["Power", v.power]].map(([k, val]) => (
                      <div key={k} className="flex gap-3">
                        <dt className="w-16 shrink-0 font-mono2 text-[11px] uppercase tracking-widest text-slate-400 pt-0.5">{k}</dt>
                        <dd className="font-medium text-slate-700">{val}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}><div className="mt-6 rounded-3xl border border-slate-200 bg-white p-4 md:p-8"><TesterVariants /></div></Reveal>
        </div>
      </section>

      {/* Exploded */}
      <section className="py-14 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={Boxes} eyebrow="Exploded · assembly stack" title="Component stack, top to base" />
          <Reveal delay={0.08}><div className="mt-6 rounded-3xl border border-slate-200 bg-white p-4 md:p-6 max-w-2xl mx-auto"><TesterExploded /></div></Reveal>
        </div>
      </section>

      {/* Full component table */}
      <section className="py-14 bg-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={Boxes} eyebrow="Bill of components" title="Every part, grouped by system" />
          <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {Object.entries(GRP_META).map(([g, m]) => {
              const items = COMPONENTS.filter((c) => c.grp === g);
              return (
                <Reveal key={g}>
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 h-full" data-testid={`bom-${g}`}>
                    <div className="flex items-center gap-2 mb-3"><span className="h-2.5 w-2.5 rounded-full" style={{ background: m.color }} /><h3 className="font-display font-bold text-slate-900">{m.label}</h3></div>
                    <ul className="space-y-2">
                      {items.map((c) => (
                        <li key={c.n} className="flex items-start gap-2.5 text-sm">
                          <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-white font-mono2 text-[10px] font-bold">{c.n}</span>
                          <span><span className="font-semibold text-slate-800">{c.name}</span><span className="text-slate-500"> — {c.fn}</span></span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-6 text-xs text-slate-400 max-w-3xl">Concept-stage engineering intent. Dimensions and component choices are indicative and to be validated with the manufacturing partner during DFM and prototyping.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/shop/brief" className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-6 py-3.5 rounded-full font-semibold transition-colors" data-testid="build-to-brief-btn">Manufacturing brief (RFQ) →</Link>
            <Link to="/software" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-sky-700 text-white px-6 py-3.5 rounded-full font-semibold transition-colors" data-testid="build-to-software-btn">See the software it runs →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
